// Payload mínimo do QR para o fluxo de trocas presenciais.
// Não contém contagens de figurinhas — a baixa é feita via RPC após o scan.
// Validade: 10 minutos a partir de `ts`.

export const QR_PAYLOAD_VERSION = 2;
export const MATCH_PATH_PREFIX = '/m/';
export const QR_VALIDITY_MS = 10 * 60 * 1000;

export interface QrPayload {
  v: 2;
  u: string;   // user_id
  n: string;   // nonce uuid (anti-replay)
  ts: number;  // unix ms
}

export interface QrPayloadInput {
  userId: string;
}

function bytesToBase64url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlToBytes(s: string): Uint8Array {
  let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function jsonToBase64url(json: string): string {
  return bytesToBase64url(new TextEncoder().encode(json));
}

function base64urlToJson(b64: string): string | null {
  try {
    return new TextDecoder().decode(base64urlToBytes(b64));
  } catch {
    return null;
  }
}

function getBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://boratrocarfigurinhas.com.br';
}

function generateNonce(): string {
  const cryptoApi = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  // Fallback raso: 16 bytes em hex.
  const arr = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(arr);
  else for (let i = 0; i < 16; i++) arr[i] = Math.floor(Math.random() * 256);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function encodeQrPayload(input: QrPayloadInput): string {
  const payload: QrPayload = {
    v: QR_PAYLOAD_VERSION,
    u: input.userId,
    n: generateNonce(),
    ts: Date.now(),
  };
  return `${getBaseUrl()}${MATCH_PATH_PREFIX}${jsonToBase64url(JSON.stringify(payload))}`;
}

function tryParseJsonPayload(json: string): QrPayload | null {
  try {
    const obj = JSON.parse(json);
    if (!obj || obj.v !== QR_PAYLOAD_VERSION) return null;
    if (typeof obj.u !== 'string') return null;
    if (typeof obj.n !== 'string') return null;
    if (typeof obj.ts !== 'number') return null;
    return obj as QrPayload;
  } catch {
    return null;
  }
}

export function decodeQrPayload(text: string): QrPayload | null {
  const trimmed = (text || '').trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('{')) return tryParseJsonPayload(trimmed);

  try {
    const url = new URL(trimmed);
    const m = url.pathname.match(/\/m\/([A-Za-z0-9_-]+)/);
    if (m) {
      const json = base64urlToJson(m[1]);
      if (json) return tryParseJsonPayload(json);
    }
  } catch {
    /* not a URL */
  }

  if (/^[A-Za-z0-9_-]{16,}$/.test(trimmed)) {
    const json = base64urlToJson(trimmed);
    if (json) return tryParseJsonPayload(json);
  }

  return null;
}

export function isPayloadFresh(payload: QrPayload): boolean {
  return Date.now() - payload.ts < QR_VALIDITY_MS;
}
